import { ReactNode, useEffect, useRef, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { SideColumn } from './SideColumn';
import { TopBar } from './TopBar';
import Feedback from '../../../Feedback/components/Feedback';
import { HelpButton } from '../HelpButton/HelpButton';
import SearchHistoryModal from '@/features/Search/components/SearchHistoryModal/SearchHistoryModal.tsx';

const Layout = ({
  onSearch,
  children,
  leftContent,
  rightContent,
  showBackButtonLink,
  contentClassName,
  originalSearchId,
  showHelpButton = true,
  leftButtonTitle,
  rightButtonTitle,
}: {
  onSearch: () => void;
  query?: string;
  children: ReactNode;
  leftContent?: () => ReactNode;
  rightContent?: () => ReactNode;
  showBackButtonLink?: string;
  originalSearchId?: string;
  leftButtonTitle?: string;
  rightButtonTitle?: string;
  contentClassName?: string;
  showHelpButton?: boolean;
}) => {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLHeadElement>(null);

  const [leftWidth, setLeftWidth] = useState<number>(0);
  const [rightWidth, setRightWidth] = useState<number>(0);
  const [topBarHeight, setTopBarHeight] = useState<number>(0);
  const [isSearchHistoryModalOpened, setSearchHistoryModalOpened] = useState(false);
  const [leftButtonActive, setLeftButtonActive] = useState(false);
  const [rightButtonActive, setRightButtonActive] = useState(false);

  useEffect(() => {
    const updateDimensions = () => {
      if (topBarRef.current) setTopBarHeight(topBarRef.current.clientHeight);
      if (leftRef.current) setLeftWidth(leftRef.current.clientWidth);
      if (rightRef.current) setRightWidth(rightRef.current.clientWidth);
    };

    const resizeObserver = new ResizeObserver(updateDimensions);
    window.addEventListener('resize', updateDimensions);

    if (topBarRef.current) resizeObserver.observe(topBarRef.current);
    if (leftRef.current) resizeObserver.observe(leftRef.current);
    if (rightRef.current) resizeObserver.observe(rightRef.current);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const handleSearchHistoryModalStatus = () => {
    setSearchHistoryModalOpened(!isSearchHistoryModalOpened);
  };

  return (
    <main id="main" className="flex flex-col items-center" style={{ paddingTop: topBarHeight }}>
      <TopBar
        onSearch={onSearch}
        ref={topBarRef}
        showBackButtonLink={showBackButtonLink}
        originalSearchId={originalSearchId}
        onOpenSearchHistoryModal={handleSearchHistoryModalStatus}
        setLeftButtonActive={setLeftButtonActive}
        leftButtonActive={leftButtonActive}
        leftButtonTitle={leftButtonTitle}
        setRightButtonActive={setRightButtonActive}
        rightButtonActive={rightButtonActive}
        rightButtonTitle={rightButtonTitle}
      />

      <div
        className="flex w-full"
        style={{
          height: `calc(100vh - ${topBarHeight}px - 1rem)`,
        }}>
        <div className="flex-1" ref={leftRef} />
        <div
          className={`${contentClassName} w-[calc(100%-700px)] max-w-[850px] min-w-[90%] md:min-w-[75%] lg:min-w-[600px]`}>
          {children}
        </div>
        <div className="flex-1" ref={rightRef} />
      </div>

      <div
        className="fixed left-0 bottom-0"
        style={{
          top: topBarHeight,
          width: leftWidth - 8,
        }}>
        <SideColumn side="left" overrideActive={leftButtonTitle ? leftButtonActive : undefined}>
          {leftContent && leftContent()}
        </SideColumn>
      </div>

      <div
        className="fixed right-0 bottom-0"
        style={{
          top: topBarHeight,
          width: rightWidth - 8,
        }}>
        <SideColumn side="right" overrideActive={rightButtonTitle ? rightButtonActive : undefined}>
          {rightContent && rightContent()}
        </SideColumn>
      </div>
      {isSearchHistoryModalOpened && (
        <SearchHistoryModal onClose={handleSearchHistoryModalStatus} onSearch={onSearch} />
      )}
      <Feedback />
      {showHelpButton && <HelpButton hideDetails />}
      <ToastContainer className="pointer-events-auto" />
    </main>
  );
};

export default Layout;
